import { useId } from 'react';

export const qronMarkPaths = {
  ring: 'M22.031 34.762c-7.035 0-12.742-5.703-12.742-12.742 0-7.036 5.707-12.743 12.742-12.743v-2.43c-8.379 0-15.172 6.794-15.172 15.173 0 8.378 6.793 15.175 15.172 15.175h15.961a21 21 0 0 0 2-2.433H22.027Z',
  crescent:
    'M17.59 17.867c8.285 0 15.015 6.64 15.164 14.89a15.12 15.12 0 0 0 4.45-10.734c0-8.378-6.794-15.171-15.173-15.171S6.86 13.645 6.86 22.023c0 .098.004.188.004.282a15.13 15.13 0 0 1 10.723-4.438Z',
  blade:
    'M36.469 26.668c.347-1.078.57-2.211.668-3.375-2.965-.137-1.996-2.258-6.301-7.98-1.945-2.586-3.809-5.36-5.68-8.133a15 15 0 0 0-3.136-.325q-.177.001-.352.008c9.691 12.403 13.582 15.606 9.652 19.73.043.09.078.192.117.282q.175.4.333.816c.015.04.023.07.039.11.132.367.257.738.363 1.12 1.566-.913 3.016-1.683 4.285-2.245Z',
  brackets: ['M5 15V5H15', 'M49 5H59V15', 'M59 49V59H49', 'M15 59H5V49'],
  glitch: ['M3.5 30.5H9', 'M55 41H60.5'],
};

export const qronMarkColors = {
  ring: '#22E5D4',
  crescentFrom: '#C04CFF',
  crescentTo: '#5B5CFF',
  blade: '#FF3DCB',
  chip: '#05080D',
};

export const qronMarkLayout = {
  scale: 1.18,
  offset: 32 - 22 * 1.18,
  sliceTop: 27,
  sliceHeight: 3.2,
  sliceShift: 2.6,
};

export function QronMark({
  size = 32,
  chip = false,
  title,
}: {
  size?: number;
  chip?: boolean;
  title?: string;
}) {
  const id = useId().replace(/:/g, '');
  const ref = (name: string) => `qs-mark-${name}-${id}`;
  const { scale, offset, sliceTop, sliceHeight, sliceShift } = qronMarkLayout;
  const place = `translate(${offset} ${offset}) scale(${scale})`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      <defs>
        <filter id={ref('glow')} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="1.2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <radialGradient id={ref('halo')} cx="0.5" cy="0.45" r="0.55">
          <stop offset="0" stopColor={qronMarkColors.ring} stopOpacity="0.22" />
          <stop offset="1" stopColor={qronMarkColors.ring} stopOpacity="0" />
        </radialGradient>
        <linearGradient id={ref('crescent')} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={qronMarkColors.crescentFrom} />
          <stop offset="1" stopColor={qronMarkColors.crescentTo} />
        </linearGradient>
        <clipPath id={ref('rest')}>
          <rect x="0" y="0" width="64" height={sliceTop} />
          <rect x="0" y={sliceTop + sliceHeight} width="64" height={64 - sliceTop - sliceHeight} />
        </clipPath>
        <clipPath id={ref('slice')}>
          <rect x="0" y={sliceTop} width="64" height={sliceHeight} />
        </clipPath>
        <g id={ref('shapes')}>
          <path
            d={qronMarkPaths.ring}
            fill={qronMarkColors.blade}
            fillOpacity="0.5"
            transform="translate(0.9 -0.6)"
          />
          <g filter={`url(#${ref('glow')})`}>
            <path d={qronMarkPaths.crescent} fill={`url(#${ref('crescent')})`} fillRule="evenodd" />
            <path d={qronMarkPaths.ring} fill={qronMarkColors.ring} />
            <path d={qronMarkPaths.blade} fill={qronMarkColors.blade} fillRule="evenodd" />
          </g>
        </g>
      </defs>
      {chip ? (
        <>
          <rect x="0.5" y="0.5" width="63" height="63" rx="14" fill={qronMarkColors.chip} />
          <rect x="0.5" y="0.5" width="63" height="63" rx="14" fill={`url(#${ref('halo')})`} />
          <rect
            x="0.5"
            y="0.5"
            width="63"
            height="63"
            rx="14"
            stroke={qronMarkColors.ring}
            strokeOpacity="0.35"
          />
          {qronMarkPaths.brackets.map((d) => (
            <path key={d} d={d} stroke={qronMarkColors.ring} strokeOpacity="0.55" strokeWidth="1.5" />
          ))}
        </>
      ) : null}
      <g clipPath={`url(#${ref('rest')})`}>
        <use href={`#${ref('shapes')}`} transform={place} />
      </g>
      <g clipPath={`url(#${ref('slice')})`}>
        <use href={`#${ref('shapes')}`} transform={`translate(${sliceShift} 0) ${place}`} />
      </g>
      {qronMarkPaths.glitch.map((d, i) => (
        <path
          key={d}
          d={d}
          stroke={i === 0 ? qronMarkColors.ring : qronMarkColors.blade}
          strokeOpacity="0.85"
          strokeWidth="1.4"
        />
      ))}
    </svg>
  );
}
